/* eslint-disable quotes */
import { useLocation } from '@reach/router';
import { useMemo } from 'react';

/**
 * A single sample question, with both the full suggestion
 * and its 20–30‑char “short” version.
 */
export interface SampleQuestion {
  /** The full suggested question text */
  question: string;
  /** A condensed version (20–30 chars) */
  shortVersion: string;
}

/**
 * Define every route (exact or “startsWith”) and its questions.
 * Order matters: more specific paths should come first.
 */
const ROUTE_QUESTIONS: Array<{
  /** match exactly or as a prefix */
  path: string;
  /** if exactOnly = true, only exact match; if false, match prefix */
  exactOnly?: boolean;
  questions: SampleQuestion[];
}> = [
  // === PDPs ===
  {
    path: '/photo-books/wedding-layflat-photo-album',
    exactOnly: true,
    questions: [
      {
        question: "What's the difference between the Wedding Layflat Photo Album and the Layflat Photo Album?",
        shortVersion: 'Wedding Layflat vs. Layflat'
      },
      {
        question: 'Which size album is best for my photos?',
        shortVersion: 'Which album size should I choose?'
      }
    ]
  },
  {
    path: '/photo-books/layflat-photo-album',
    exactOnly: true,
    questions: [
      {
        question: "What's the difference between the Wedding Layflat Photo Album and the Layflat Photo Album?",
        shortVersion: 'Wedding Layflat vs. Layflat'
      },
      {
        question: 'Which size album is best for my photos?',
        shortVersion: 'Which album size should I choose?'
      }
    ]
  },
  {
    path: '/photo-books/hardcover-books',
    exactOnly: true,
    questions: [
      {
        question: 'What’s the difference between the Hardcover Photo Book and the Photo‑Wrapped Hardcover Book?',
        shortVersion: 'Hardcover vs. Photo‑Wrapped Hardcover'
      },
      {
        question: 'How many photos can I fit in a Hardcover Photo Book?',
        shortVersion: 'How many photos can I fit?'
      }
    ]
  },
  {
    path: '/photo-books/photo-wrapped-hardcover',
    exactOnly: true,
    questions: [
      {
        question: 'What’s the difference between the Hardcover Photo Book and the Photo‑Wrapped Hardcover Book?',
        shortVersion: 'Hardcover vs. Photo‑Wrapped Hardcover'
      },
      {
        question: 'How many photos can I fit in a Photo‑Wrapped Hardcover Book?',
        shortVersion: 'How many photos can I fit?'
      }
    ]
  },
  {
    path: '/photo-prints/classic-prints',
    exactOnly: true,
    questions: [
      {
        question: 'What’s the difference between Everyday and Lustre Photo Prints?',
        shortVersion: 'Everyday vs. Lustre Photo Prints'
      },
      {
        question: 'What size Everyday Print fits in the Photo Blocks?',
        shortVersion: 'What size should I print?'
      }
    ]
  },
  {
    path: '/photo-prints/lustre-photo-prints',
    exactOnly: true,
    questions: [
      {
        question: 'What’s the difference between Everyday and Lustre Photo Prints?',
        shortVersion: 'Everyday vs. Lustre Photo Prints'
      },
      {
        question: 'What size Everyday Print fits in the Photo Blocks?',
        shortVersion: 'What size should I print?'
      }
    ]
  },

  // === CLPs (category landing pages) ===
  {
    path: '/photo-books',
    questions: [
      {
        question: "What's the difference between Layflat and Hardcover photo books?",
        shortVersion: 'Layflat vs Hardcover books'
      },
      {
        question: 'What book is best to print my travel photos?',
        shortVersion: 'Travel photo books'
      },
      {
        question: 'Can you help me pick the right photo book for me?',
        shortVersion: 'Which book should I choose?'
      }
    ]
  },
  {
    path: '/photo-prints',
    questions: [
      {
        question: "What's the difference between Everyday and Lustre Photo Prints?",
        shortVersion: 'Difference in prints paper'
      },
      {
        question: 'How can I display my photo prints?',
        shortVersion: 'How can I display my prints?'
      },
      {
        question: 'How many photos fit in the Photo Blocks?',
        shortVersion: 'How many photos fit?'
      }
    ]
  },
  {
    path: '/wedding/photo-albums',
    questions: [
      {
        question: 'Can you help me pick the right wedding album for my photos?',
        shortVersion: 'Which album is right for me?'
      },
      {
        question:
          'What’s the difference between the Wedding Layflat Photo Album and the Signature Layflat Photo Album?',
        shortVersion: 'Wedding Layflat vs Signature Layflat'
      }
    ]
  },
  {
    path: '/frames',
    questions: [
      {
        question: 'Can you help me create a gallery wall for my living room?',
        shortVersion: 'Create a gallery wall'
      },
      {
        question: 'What’s the difference between the Gallery Frame and the Banister Wood Frame?',
        shortVersion: 'Compare wood frames'
      }
    ]
  },
  {
    path: '/photo-calendars',
    questions: [
      {
        question: "I'm looking for a calendar to display on my desk.",
        shortVersion: 'Desktop calendar options'
      },
      {
        question: "I'm looking for a calendar to hang on my wall.",
        shortVersion: 'Wall calendar options'
      }
    ]
  },
  {
    path: '/photo-cards',
    questions: [
      {
        question: 'What Thank You cards do you have for weddings?',
        shortVersion: 'Wedding thank you cards'
      },
      {
        question: 'Can I upload my own design to a card?',
        shortVersion: 'Design your own cards'
      }
    ]
  },
  {
    path: '/photo-gifts',
    questions: [
      {
        question: "I want to give a personalized gift for Mother's Day under $50.",
        shortVersion: "Mother's Day gifts under $50"
      },
      {
        question: 'Help me choose a gift for a new mom.',
        shortVersion: 'Gifts for New Moms'
      }
    ]
  },
  {
    path: '/wedding',
    questions: [
      {
        question: 'Can you help me pick the right wedding album for my photos?',
        shortVersion: 'Which album is right for me?'
      },
      {
        question: 'What guest book options do you have?',
        shortVersion: 'Guest Book options'
      }
    ]
  },

  // === Homepage fallback ===
  {
    path: '/',
    questions: [
      {
        question: "I want to give a personalized gift for Mother's Day under $50.",
        shortVersion: "Mother's Day gifts under $50"
      },
      {
        question: "What's the best album to print my wedding photos?",
        shortVersion: 'Wedding album options'
      },
      {
        question: 'What photo book is best for my everyday photos?',
        shortVersion: 'Print photos from my phone'
      }
    ]
  }
];

/**
 * Custom hook: returns the matching array of SampleQuestion
 * for the current `location.pathname`.
 */
export function useSampleQuestions(): SampleQuestion[] {
  const { pathname } = useLocation();

  return useMemo(() => {
    // Try to find the first entry where:
    // - exactOnly && pathname === path, OR
    // - !exactOnly && pathname.startsWith(path)
    for (const entry of ROUTE_QUESTIONS) {
      const matchExact = entry.exactOnly
        ? pathname === entry.path
        : pathname === entry.path || pathname.startsWith(entry.path);
      if (matchExact) {
        return entry.questions;
      }
    }
    // If nothing matched (shouldn't happen as "/" is last), return empty
    return [];
  }, [pathname]);
}
